<template>
    <div id="user-item" class="p-2 h-full w-full" @mouseover="isHover = true" @mouseleave="isHover = false" @click="() => { if (!isHoverTrash) {$emit('clicked-item')} }">
        <div class="w-full flex flex-row justify-content-between align-items-center">
            <div class="flex flex-row align-items-center gap-2 w-full">
                <!-- Avatar -->
                <Avatar v-if="isUser" class="avatar" size="small" shape="circle" :class="{'is-selected-user': isSelected}">
                    <SvgUserMe className="avatar-me-svg" color="var(--theme-font-color)"/>
                </Avatar>
                <Avatar v-else class="avatar" :label="getAvatarLabelFromFirstNameAndLastName(userItem.first_name, userItem.last_name)" size="small" shape="circle" :class="{'is-selected-user': isSelected}"/>

                <!-- Username/Email block -->
                <div class="flex flex-column align-items-between justify-content-center overflow-hidden" :style="`max-width: ${(isUser || isHover) ? 11.5 : 14.5}rem`">
                    <div class="flex flex-row gap-2">
                        <span style="font-size: var(--users-item-username-font-size); font-weight: var(--users-item-username-font-weight);" class="potentially-long-label">{{ userItem.first_name }} {{ userItem.last_name }}</span>
                    </div>
                    <span style="font-size: var(--users-item-email-font-size); font-weight: var(--users-item-email-font-weight);" class="potentially-long-label">{{ userItem.email }}</span>
                </div>
            </div>
            <span v-if="isUser" style="font-size: var(--users-item-email-font-size); font-weight: var(--users-item-email-font-weight); font-style: italic;" class="mr-1" >(You)</span>

            <div v-else-if="isHover">
                <div v-if="!isMyUser || isMyOwner || isMyUserActive|| isInMyIndirectlyLinkedUsers ">
                    <div 
                        v-tooltip.left="{
                            value: `${
                                        isMyOwner ? 'You cannot remove one of your owners. If you really want to remove this user, please first remove all the licenses they have delegated to you.' :
                                        !isMyUser ? 'You can only remove one of your users.' :
                                        isMyUserActive ? 'You can not remove a user that is currently running one of your licenses.' :
                                        isInMyIndirectlyLinkedUsers ? 'You can not remove a user that shares at least one license by delegation with you.' :
                                        'You can not remove this user.'
                                    }`,
                            showDelay: 700
                        }">
                            <Button text id="delete-user-button" rounded icon="pi pi-trash" @click="$emit('delete')" disabled style="padding: 0; border-radius: 980px;">
                                <SvgTrash className="table-licenses-svg" :color="isHoverTrash ? 'var(--button-danger-color)' : 'var(--button-normal-color)'"/>
                            </Button>
                    </div>
                </div>
                <div v-else>
                    <Button text id="delete-user-button" rounded icon="pi pi-trash" @click="$emit('delete')" style="padding: 0; border-radius: 980px;" @mouseover="isHoverTrash = true" @mouseleave="isHoverTrash = false">
                        <SvgTrash className="table-licenses-svg" :color="isHoverTrash ? 'var(--button-danger-color)' : 'var(--button-normal-color)'"/>
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, toRefs, computed } from 'vue';
// PrimeVue
import Avatar from 'primevue/avatar'
import Button from 'primevue/button'
// vue
import SvgTrash from '@/components/SVGs/SvgTrash.vue'
import SvgUserMe from '@/components/SVGs/SvgUserMe.vue'
// Others
import AvatarsUtils from '@/composables/AvatarsUtils'
import { useStore } from 'vuex';
import UsersUtils from '@/composables/UsersUtils';

const store = useStore()
const { myUsers, myOwners, myIndirectlyLinkedUsers } = UsersUtils(store)

const { getAvatarLabelFromFirstNameAndLastName } = AvatarsUtils()

// --- PROPS ---
const props = defineProps({
    userItem: Object,
    isSelected: Boolean,
})
const { userItem } = toRefs(props)

// --- EMITS ---
const emit = defineEmits(['clicked-item', 'delete'])

// --- Computed ---
const user = computed(() => store.state.user.user)
const isUser = computed(() => user.value.id === userItem.value.id)
const isMyUser = computed(() => myUsers.value.some((u) => u.id === userItem.value.id))
const isMyOwner = computed(() => myOwners.value.some((u) => u.id === userItem.value.id))
const isMyUserActive = computed(() => store.state.licenses.licenses.some((license) => (license.admin.id === user.value.id) && license.current_status.activated && license.current_status.activeUser && ((license.current_status.activeUser.id ?? "-1") === userItem.value.id)))
const isInMyIndirectlyLinkedUsers = computed(() =>  myIndirectlyLinkedUsers.value.some((u) => u.id === userItem.value.id))

// --- VARIABLE ---
const isHover = ref(false)
const isHoverTrash = ref(false)
</script>

<style scoped>
#user-item {
    color: var(--theme-font-color);
}
#delete-user-button:not(.p-disabled):hover {
    color: var(--button-danger-color);
}
#delete-user-button.p-disabled {
    opacity: 0.5;
}
</style>